import { createError } from '@common/neo.tax';
import { Spinner, Stack } from '@ui-components/neo.tax';
import { ReactElement } from 'react';

import { type AscProject, useAscProject } from '@neo.tax/asc/hooks';

import { Modal } from './capitalization-edit-modal.styled';
import { CapitalizationEditModalContent } from './CapitalizationEditModalContent';

type CapitalizationEditModalProps = {
  isOpen: boolean;
  onClose: () => void;
} & ({ ascProjectId: string; ascProject?: never } | { ascProject: AscProject; ascProjectId?: never });

/**
 * Modal for editing ASC project capitalization settings.
 * Can be used with either ascProjectId (will load the project) or ascProject (uses provided data).
 */
export const CapitalizationEditModal = ({
  ascProject: providedAscProject,
  ascProjectId,
  isOpen,
  onClose,
}: CapitalizationEditModalProps): ReactElement | null => {
  const { ascProject: fetchedAscProject, loading } = useAscProject(ascProjectId ?? providedAscProject?.id ?? '');

  // Use provided ascProject if available, otherwise use fetched one
  const ascProject = providedAscProject ?? fetchedAscProject;

  // Don't render anything if modal is closed
  if (!isOpen) {
    return null;
  }

  // Show loading state while fetching project (only when using ascProjectId)
  if (!providedAscProject && loading) {
    return (
      <Modal isOpen={isOpen}>
        <Stack alignItems="center" justifyContent="center" paddingY={10}>
          <Spinner />
        </Stack>
      </Modal>
    );
  }

  // If project not found, don't render
  if (!ascProject) {
    throw createError('CapitalizationEditModalError', 'ASC project not found');
  }

  return <CapitalizationEditModalContent isOpen={isOpen} onClose={onClose} ascProject={ascProject} />;
};
